import { createRouter, createWebHistory } from 'vue-router';
import { initializeAuth } from '@/stores/auth-init';

// Lazy load components
const Home = () => import('@/views/Home.vue');
const Dashboard = () => import('@/views/Dashboard.vue');
const ChecklistBuilder = () => import('@/views/checklist/ChecklistBuilder.vue');
const Login = () => import('@/views/Login.vue');
const Register = () => import('@/views/Register.vue');
const RegisterInterest = () => import('@/views/RegisterInterest.vue');
const ForgotPassword = () => import('@/views/ForgotPassword.vue');
const ResetPassword = () => import('@/views/ResetPassword.vue');
const EmailConfirmation = () => import('@/views/EmailConfirmation.vue');
const ConfirmEmailChange = () => import('@/views/ConfirmEmailChange.vue');
const About = () => import('@/views/About.vue');
const Features = () => import('@/views/Features.vue');
const Pricing = () => import('@/views/Pricing.vue');
const Terms = () => import('@/views/Terms.vue');
const Privacy = () => import('@/views/Privacy.vue');
const AccountSubscription = () => import('@/views/AccountSubscription.vue');
const AccountProfile = () => import('@/views/AccountProfile.vue');
const AccountOverview = () => import('@/views/AccountOverview.vue');
const AccountPassword = () => import('@/views/AccountPassword.vue');
const SignOutSuccess = () => import('@/views/SignOutSuccess.vue');
const OnboardingRegistration = () => import('@/views/employee_portal/OnboardingRegistration.vue');
const EmployeeChecklist = () => import('@/views/employee_portal/EmployeeChecklistTwoStep.vue');
const CompanyInvitationAcceptance = () => import('@/views/CompanyInvitationAcceptance.vue');
const CompanyInvitationRegistration = () => import('@/views/CompanyInvitationRegistration.vue');
const CompanyMembers = () => import('@/views/CompanyMembers.vue');
const MemberProfile = () => import('@/views/MemberProfile.vue');
const SuperAdminDashboard = () => import('@/views/SuperAdminDashboard.vue');

// Blog posts
const TheNewWorkplaceRulesWhatEmployeesNeedToKnowIn2027 = () => import('@/views/blog/TheNewWorkplaceRulesWhatEmployeesNeedToKnowIn2027.vue');
const UsingAIAtWorkWhatsHelpfulWhatsRiskyAndWhatToAvoid = () => import('@/views/blog/UsingAIAtWorkWhatsHelpfulWhatsRiskyAndWhatToAvoid.vue');
const TenPracticalTipsForAStressFreeFirstDayAtANewJob = () => import('@/views/blog/TenPracticalTipsForAStressFreeFirstDayAtANewJob.vue');
const TheUltimateOnboardingChecklistForEmployers = () => import('@/views/blog/TheUltimateOnboardingChecklistForEmployers.vue');
const HowToMakeAGreatFirstImpression = () => import('@/views/blog/HowToMakeAGreatFirstImpression.vue');
const HowToBuildStrongRelationshipsWithYourNewColleagues = () => import('@/views/blog/HowToBuildStrongRelationshipsWithYourNewColleagues.vue');
const FromNewHireToHighPerformerHowToStandOutInYourFirstYear = () => import('@/views/blog/FromNewHireToHighPerformerHowToStandOutInYourFirstYear.vue');
const NavigatingOfficePoliticsAGuideForNewEmployees = () => import('@/views/blog/NavigatingOfficePoliticsAGuideForNewEmployees.vue');
const TimeManagementHacksForNewEmployees = () => import('@/views/blog/TimeManagementHacksForNewEmployees.vue');
const SkillsEveryNewEmployeeShouldDevelopThisYear = () => import('@/views/blog/SkillsEveryNewEmployeeShouldDevelopThisYear.vue');

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { public: true }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { public: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { public: true }
  },
  // {
  //   path: '/register-interest',
  //   name: 'RegisterInterest',
  //   component: RegisterInterest,
  //   props: (route) => ({
  //     step: route.query.step || '1'
  //   }),
  //   meta: { public: true }
  // },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { public: true }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    props: (route) => ({
      token: route.query.token || '',
      email: route.query.email || ''
    }),
    meta: { public: true }
  },
  {
    path: '/about',
    name: 'About',
    component: About,
    meta: { public: true }
  },
  {
    path: '/confirm-email',
    name: 'EmailConfirmation',
    component: EmailConfirmation,
    props: (route) => ({
      token: route.query.token,
      email: route.query.email
    }),
    meta: { public: true }
  },
  {
    path: '/confirm-email-change',
    name: 'ConfirmEmailChange',
    component: ConfirmEmailChange,
    props: (route) => ({
      token: route.query.token,
      email: route.query.email
    }),
    meta: { public: true }
  },
  {
    path: '/features',
    name: 'Features',
    component: Features,
    meta: { public: true }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: Pricing,
    meta: { public: true }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: Terms,
    meta: { public: true }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: Privacy,
    meta: { public: true }
  },
  {
    path: '/account/subscription',
    name: 'AccountSubscription',
    component: AccountSubscription,
    meta: { requiresAuth: true, requiresRole: ['owner', 'admin'] }
  },
  {
    path: '/account/password',
    name: 'AccountPassword',
    component: AccountPassword,
    meta: { requiresAuth: true }
  },
  {
    path: '/account/profile',
    name: 'AccountProfile',
    component: AccountProfile,
    meta: { requiresAuth: true }
  },
  {
    path: '/account/overview',
    name: 'AccountOverview',
    component: AccountOverview,
    meta: { requiresAuth: true }
  },
  {
    path: '/sign-out-success',
    name: 'SignOutSuccess',
    component: SignOutSuccess,
    meta: { public: true }
  },
  {
    path: '/onboarding/:token',
    name: 'OnboardingRegistration',
    component: OnboardingRegistration,
    meta: { public: true }
  },
  {
    path: '/company-invitation/:token',
    name: 'CompanyInvitationAcceptance',
    component: CompanyInvitationAcceptance,
    meta: { public: true }
  },
  {
    path: '/company-invitation/:token/register',
    name: 'CompanyInvitationRegistration',
    component: CompanyInvitationRegistration,
    meta: { public: true }
  },
  {
    path: '/employee/checklists/:id',
    name: 'EmployeeChecklist',
    component: EmployeeChecklist,
    meta: { requiresAuth: true }
  },
  {
    path: '/checklists/builder/:id?',
    name: 'ChecklistBuilder',
    component: ChecklistBuilder,
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/company',
    name: 'CompanyMembers',
    component: CompanyMembers,
    meta: { requiresAuth: true, requiresRole: ['owner', 'admin', 'member'] }
  },
  {
    path: '/company/members/:id',
    name: 'MemberProfile',
    component: MemberProfile,
    props: true,
    meta: { requiresAuth: true, requiresRole: ['owner', 'admin', 'member'] }
  },
  {
    path: '/super-admin',
    name: 'SuperAdminDashboard',
    component: SuperAdminDashboard,
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  // Blog post routes
  {
    path: '/blog/the-new-workplace-rules-what-employees-need-to-know-in-2027',
    name: 'TheNewWorkplaceRulesWhatEmployeesNeedToKnowIn2027',
    component: TheNewWorkplaceRulesWhatEmployeesNeedToKnowIn2027,
    meta: { public: true }
  },
  {
    path: '/blog/using-ai-at-work-whats-helpful-whats-risky-and-what-to-avoid',
    name: 'UsingAIAtWorkWhatsHelpfulWhatsRiskyAndWhatToAvoid',
    component: UsingAIAtWorkWhatsHelpfulWhatsRiskyAndWhatToAvoid,
    meta: { public: true }
  },
  {
    path: '/blog/ten-practical-tips-for-a-stress-free-first-day-at-a-new-job',
    name: 'TenPracticalTipsForAStressFreeFirstDayAtANewJob',
    component: TenPracticalTipsForAStressFreeFirstDayAtANewJob,
    meta: { public: true }
  },
  {
    path: '/blog/the-ultimate-onboarding-checklist-for-employers',
    name: 'TheUltimateOnboardingChecklistForEmployers',
    component: TheUltimateOnboardingChecklistForEmployers,
    meta: { public: true }
  },
  {
    path: '/blog/how-to-make-a-great-first-impression',
    name: 'HowToMakeAGreatFirstImpression',
    component: HowToMakeAGreatFirstImpression,
    meta: { public: true }
  },
  {
    path: '/blog/how-to-build-strong-relationships-with-your-new-colleagues',
    name: 'HowToBuildStrongRelationshipsWithYourNewColleagues',
    component: HowToBuildStrongRelationshipsWithYourNewColleagues,
    meta: { public: true }
  },
  {
    path: '/blog/from-new-hire-to-high-performer-how-to-stand-out-in-your-first-year',
    name: 'FromNewHireToHighPerformerHowToStandOutInYourFirstYear',
    component: FromNewHireToHighPerformerHowToStandOutInYourFirstYear,
    meta: { public: true }
  },
  {
    path: '/blog/navigating-office-politics-a-guide-for-new-employees',
    name: 'NavigatingOfficePoliticsAGuideForNewEmployees',
    component: NavigatingOfficePoliticsAGuideForNewEmployees,
    meta: { public: true }
  },
  {
    path: '/blog/time-management-hacks-for-new-employees',
    name: 'TimeManagementHacksForNewEmployees',
    component: TimeManagementHacksForNewEmployees,
    meta: { public: true }
  },
  {
    path: '/blog/skills-every-new-employee-should-develop-this-year',
    name: 'SkillsEveryNewEmployeeShouldDevelopThisYear',
    component: SkillsEveryNewEmployeeShouldDevelopThisYear,
    meta: { public: true }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Always scroll to top when navigating to a new route
    return { top: 0 };
  }
});

// Navigation guard
router.beforeEach(async (to, from, next) => {
  // Check if route requires authentication
  const isPublicRoute = to.matched.some(record => record.meta.public);
  const isAuthRequired = to.matched.some(record => record.meta.requiresAuth);
  const isDisabled = to.matched.some(record => record.meta.disabled);
  const requiredRoles = to.matched.find(record => record.meta.requiresRole)?.meta.requiresRole;
  const requiresSuperAdmin = to.matched.some(record => record.meta.requiresSuperAdmin);

  // Initialize auth state if not already done
  const authResult = await initializeAuth();

  // Use the returned authentication state
  const isAuthenticatedAfterInit = authResult.isAuthenticated;
  const currentUserRole = authResult.user?.company?.role;

  // If route is disabled, redirect to register interest
  if (isDisabled) {
    return next({ name: 'RegisterInterest' });
  }

  // Define auth routes explicitly
  const isAuthRoute = ['Login', 'Register', 'ForgotPassword', 'ResetPassword'].includes(to.name);

  // If user is authenticated and trying to access auth routes (login/register), redirect to dashboard
  if (isAuthenticatedAfterInit && isAuthRoute) {
    return next({ name: 'Dashboard' });
  }

  // If this is a public route, allow access
  if (isPublicRoute && !isAuthRequired) {
    return next();
  }

  // If route requires authentication and user is not authenticated, redirect to login
  if (isAuthRequired && !isAuthenticatedAfterInit) {
    return next({
      name: 'Login',
      query: { redirect: to.fullPath }
    });
  }

  // Check super admin authorization
  if (requiresSuperAdmin && isAuthenticatedAfterInit) {
    if (!authResult.user?.super_admin) {
      // User is not a super admin, redirect to dashboard
      return next({ name: 'Dashboard' });
    }
  }

  // Check role-based authorization if user is authenticated and route requires specific roles
  if (isAuthenticatedAfterInit && requiredRoles && Array.isArray(requiredRoles)) {
    if (!currentUserRole || !requiredRoles.includes(currentUserRole)) {
      // User doesn't have required role, redirect to dashboard
      return next({ name: 'Dashboard' });
    }
  }

  // If user is authenticated but trying to access a non-existent route, redirect to dashboard
  if (isAuthenticatedAfterInit && !isPublicRoute && !isAuthRequired) {
    return next({ name: 'Dashboard' });
  }

  if (requiresSuperAdmin && isAuthenticatedAfterInit) {
    if (!authResult.user?.super_admin) {
      // User is not a super admin, redirect to dashboard
      return next({ name: 'Dashboard' });
    }
  }

  // Check role-based authorization if user is authenticated and route requires specific roles
  if (isAuthenticatedAfterInit && requiredRoles && Array.isArray(requiredRoles)) {
    if (!currentUserRole || !requiredRoles.includes(currentUserRole)) {
      // User doesn't have required role, redirect to dashboard
      return next({ name: 'Dashboard' });
    }
  }

  // If user is authenticated but trying to access a non-existent route, redirect to dashboard
  if (isAuthenticatedAfterInit && !isPublicRoute && !isAuthRequired) {
    return next({ name: 'Dashboard' });
  }

  next();
});

export default router;
